<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // { key, title, createdAt, post, broadcast }
  campaign: { type: Object, required: true },
});

const emit = defineEmits(['publish', 'trigger', 'preview', 'delete']);

const { t } = useI18n();

const STATUS_STYLES = {
  draft: 'text-n-slate-11',
  scheduled: 'text-n-blue-11',
  pending: 'text-n-slate-11',
  processing: 'text-n-amber-11',
  publishing: 'text-n-amber-11',
  completed: 'text-n-teal-11',
  published: 'text-n-teal-11',
  partially_published: 'text-n-amber-11',
  failed: 'text-n-ruby-11',
};

const statusClass = status => STATUS_STYLES[status] ?? 'text-n-slate-11';

const post = computed(() => props.campaign.post);
const broadcast = computed(() => props.campaign.broadcast);

const previewImage = computed(
  () =>
    post.value?.media?.find(item => item.content_type?.startsWith('image/'))
      ?.url
);

const scheduledAtLabel = computed(() => {
  const timestamp = post.value?.scheduled_at ?? broadcast.value?.scheduled_at;
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleString();
});

const caption = computed(
  () => post.value?.caption || broadcast.value?.message || ''
);

const title = computed(
  () =>
    post.value?.title ||
    broadcast.value?.title ||
    t('MARKETING.HISTORY.UNTITLED')
);

// Coincide con SocialPost#triggerable?: se puede reintentar lo que fallo o salio a
// medias, pero no lo que esta publicandose ahora.
const canPublishPost = computed(() =>
  ['draft', 'scheduled', 'failed', 'partially_published'].includes(
    post.value?.status
  )
);

// A failed broadcast can be retried: only the recipients that never produced a
// message get queued again.
const canTriggerBroadcast = computed(() =>
  ['draft', 'scheduled', 'failed'].includes(broadcast.value?.status)
);
</script>

<template>
  <CardLayout>
    <div class="flex gap-4">
      <img
        v-if="previewImage"
        :src="previewImage"
        alt=""
        class="object-cover w-20 h-20 rounded-lg shrink-0"
      />
      <div class="flex flex-col flex-1 gap-3 min-w-0">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ title }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('MARKETING.HISTORY.SCHEDULED_AT') }}: {{ scheduledAtLabel }}
          </span>
        </div>

        <p v-if="caption" class="text-sm line-clamp-2 text-n-slate-11">
          {{ caption }}
        </p>

        <!-- Un renglón por canal: es lo que hace legible una campaña que salió
             al mismo tiempo por redes y por WhatsApp. -->
        <div class="flex flex-col gap-1 text-xs">
          <div
            v-for="target in post?.targets ?? []"
            :key="`t-${target.id}`"
            class="flex flex-wrap items-center gap-2"
          >
            <span class="text-n-slate-11">
              {{ target.inbox_name }} ·
              {{
                t(`MARKETING.POSTS.PLATFORM.${target.platform.toUpperCase()}`)
              }}
            </span>
            <span :class="statusClass(target.status)">
              {{ t(`MARKETING.POSTS.STATUS.${target.status.toUpperCase()}`) }}
            </span>
            <span v-if="target.error" class="truncate text-n-ruby-11">
              {{ target.error }}
            </span>
          </div>

          <div v-if="broadcast" class="flex flex-col gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-n-slate-11">WhatsApp</span>
              <span :class="statusClass(broadcast.status)">
                {{
                  t(
                    `MARKETING.BROADCASTS.STATUS.${broadcast.status.toUpperCase()}`
                  )
                }}
              </span>
              <span class="text-n-slate-11">
                {{ t('MARKETING.BROADCASTS.CARD.SENT') }}:
                {{ broadcast.sent_count }} ·
                {{ t('MARKETING.BROADCASTS.CARD.FAILED') }}:
                {{ broadcast.failed_count }} ·
                {{ t('MARKETING.BROADCASTS.CARD.SKIPPED') }}:
                {{ broadcast.skipped_count }}
              </span>
            </div>
            <!-- Sin esto un fallo al disparar queda como "Falló" y nada más. -->
            <p v-if="broadcast.last_error" class="text-n-ruby-11">
              {{ broadcast.last_error }}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <Button
            v-if="broadcast"
            size="sm"
            variant="faded"
            color="slate"
            :label="t('MARKETING.BROADCASTS.CARD.PREVIEW')"
            @click="emit('preview', broadcast)"
          />
          <Button
            v-if="canTriggerBroadcast"
            size="sm"
            :label="t('MARKETING.BROADCASTS.CARD.SEND_NOW')"
            @click="emit('trigger', broadcast)"
          />
          <Button
            v-if="canPublishPost"
            size="sm"
            :label="t('MARKETING.POSTS.CARD.PUBLISH_NOW')"
            @click="emit('publish', post)"
          />
          <Button
            size="sm"
            variant="faded"
            color="ruby"
            :label="t('MARKETING.HISTORY.DELETE')"
            @click="emit('delete', campaign)"
          />
        </div>
      </div>
    </div>
  </CardLayout>
</template>
